import { JobResponse } from '@parkchirp/reporting';
import ky, { HTTPError } from 'ky';
import { useCallback, useMemo, useState } from 'react';

type UseCancelReportArgs = {
	baseUrl: string;
	authToken: string;
};

type UseCancelReportResult = {
	cancelReport: (jobId: string) => Promise<boolean>;
	isCancellingReport: boolean;
	error: string | null;
};

const CONFLICT_STATUS = 409;

const getErrorMessage = (error: unknown) => {
	if (error instanceof HTTPError && error.response.status === CONFLICT_STATUS) {
		return 'This report has already been generated and can no longer be canceled.';
	}

	return 'Failed to cancel report.';
};

export const useCancelReport = ({ baseUrl, authToken }: UseCancelReportArgs): UseCancelReportResult => {
	const [isCancellingReport, setIsCancellingReport] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const api = useMemo(() => {
		return ky.create({
			prefixUrl: baseUrl,
			timeout: 30000,
			retry: { limit: 0 },
			headers: {
				Authorization: `Bearer ${authToken}`,
			},
		});
	}, [authToken, baseUrl]);

	const cancelReport = useCallback(
		async (jobId: string) => {
			setIsCancellingReport(true);
			setError(null);

			try {
				await api.post(`jobs/${jobId}/cancel`).json<JobResponse>();
				return true;
			} catch (error) {
				setError(getErrorMessage(error));
				return false;
			} finally {
				setIsCancellingReport(false);
			}
		},
		[api],
	);

	return {
		cancelReport,
		isCancellingReport,
		error,
	};
};
